import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
  useSuspenseQuery
} from '@tanstack/react-query'
import { departureQueryOptions } from './data.ts'
import { DepartureList } from './components/departure-list.tsx'
import { MainContext } from './context/main.tsx'
import { FilterProvider, useFilters } from './context/filters.tsx'
import { FilterBar } from './components/filters.tsx'
import { applyFilterConfiguration } from './lib/filters.ts'
import { scrollTargetIntoView } from './lib/scroll.ts'
import { IconSprite } from './icons/icon-sprite.tsx'

class YearChangeEvent extends Event {
  constructor (public year: number) {
    super('yearchange', {
      bubbles: true,
      cancelable: false
    })
  }
}

function getRootElement (element: Element): Element {
  let current = element
  while (current.parentElement != null) {
    current = current.parentElement
  }
  return current
}

function AppContent ({ tourId, shipId, year }: { tourId?: string, shipId?: string, year?: number }): JSX.Element {
  const [filterYear, setFilterYear] = useState(year)
  const { data, isFetching, refetch } = useSuspenseQuery(departureQueryOptions({ tourId, shipId }))

  const mainRef = useRef<HTMLElement | null>(null)
  const resizeObserver = useRef<ResizeObserver | null>(null)
  const departureListRef = useRef<HTMLDivElement>(null)
  const isInitialRender = useRef(true)

  const { filters, sort, setFilter } = useFilters()

  const years = useMemo(() => {
    return Array.from(data.departures.reduce((set, { start_year: year }) => set.add(year), new Set<number>(filterYear == null ? [] : [filterYear]))).sort((a, b) => a - b)
  }, [data, filterYear])
  const filterYearDepartures = useMemo(() => filterYear != null ? data.departures.filter(({ start_year: year }) => year === filterYear) : data.departures, [data.departures, filterYear])
  const filteredDepartures = useMemo(() => applyFilterConfiguration(filterYearDepartures, filters, sort), [filterYearDepartures, filters, sort])

  const onYearChange = useCallback((year: number) => {
    setFilterYear(year)
    setFilter('date', [])
    if (mainRef.current != null) {
      const event = new YearChangeEvent(year)
      const rootElement = getRootElement(mainRef.current)
      const shadowRoot = rootElement.parentNode
      if (shadowRoot instanceof ShadowRoot) {
        const host = shadowRoot.host
        host.dispatchEvent(event)
      }
    }
  }, [setFilter])

  useLayoutEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    scrollTargetIntoView(departureListRef.current)
  }, [filters, sort])

  const mainRefCallback = useCallback((main: HTMLElement | null) => {
    resizeObserver.current ??= new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target instanceof HTMLElement) {
          entry.target.style.setProperty('--main-width', String(entry.contentBoxSize[0].inlineSize) + 'px')
        }
      }
    })
    mainRef.current = main
    if (main != null) {
      resizeObserver.current.observe(main)
    } else {
      resizeObserver.current.disconnect()
    }
  }, [])

  const onRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  return (
    <main ref={mainRefCallback}>
      <IconSprite />
      <MainContext.Provider value={{ mainRef }}>
        <FilterBar
          departures={filterYearDepartures}
          translations={data.translations}
          years={years}
          year={filterYear}
          onYearChange={onYearChange}
        />
        <DepartureList
          departures={filteredDepartures} translations={data.translations} ref={departureListRef}
        />
      </MainContext.Provider>
      {import.meta.env.DEV && (
        <>

          <pre>
            Fetching: {JSON.stringify(isFetching)}
          </pre>
          <button type='button' onClick={onRefresh}>
            Refetch
          </button>
        </>
      )}
    </main>
  )
}

function App ({ tourId, shipId, year, dehydratedState }: {
  tourId: string | null
  shipId: string | null
  year: number | null
  dehydratedState?: DehydratedState | null
}): JSX.Element {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <FilterProvider>
          <AppContent tourId={tourId ?? undefined} shipId={shipId ?? undefined} year={year ?? undefined} />
        </FilterProvider>
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default App
